import './languages.css';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import messages from './messages.json';

export const languages = [
  { code: 'fr', name: 'Français', locale: 'fr-FR' },
  { code: 'en', name: 'English', locale: 'en-GB' },
  { code: 'es', name: 'Español', locale: 'es-ES' },
  { code: 'pt', name: 'Português', locale: 'pt-PT' },
  { code: 'it', name: 'Italiano', locale: 'it-IT' },
  { code: 'de', name: 'Deutsch', locale: 'de-DE' },
  { code: 'zh', name: '中文（简体）', locale: 'zh-CN' },
  { code: 'ar', name: 'العربية', locale: 'ar' },
] as const;
export type Language = typeof languages[number]['code'];
export type Translations = Record<string, Record<Exclude<Language, 'fr'>, string>>;
const isLanguage = (value: unknown): value is Language => languages.some(item => item.code === value);
const ltrCache = createCache({ key: 'gsport-ltr', stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: 'gsport-rtl', stylisPlugins: [prefixer, rtlPlugin] });
const LanguageContext = createContext({ language: 'fr' as Language, locale: 'fr-FR' as string, direction: 'ltr' as 'ltr' | 'rtl', setLanguage: (_language: Language) => {} });

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, updateLanguage] = useState<Language>(() => {
    const linkLanguage = new URLSearchParams(window.location.search).get('lang');
    if (isLanguage(linkLanguage)) return linkLanguage;
    try { const saved = localStorage.getItem('g1sport.language'); return isLanguage(saved) ? saved : 'fr'; } catch { return 'fr'; }
  });
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const locale = languages.find(item => item.code === language)!.locale;
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    try { localStorage.setItem('g1sport.language', language); } catch { /* Private browsing can disable storage. */ }
  }, [language, locale, direction]);
  return <LanguageContext.Provider value={{ language, locale, direction, setLanguage: updateLanguage }}><CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>{children}</CacheProvider></LanguageContext.Provider>;
}

export function useLanguage() { return useContext(LanguageContext); }
export function useTranslation(catalog: Translations = messages) {
  const { language, ...rest } = useLanguage();
  return { language, ...rest, t: (source: string, values?: Record<string, string | number>) => {
    let translated = source;
    if (language !== 'fr') {
      translated = catalog[source]?.[language] ?? source;
      // Icons and field markers remain decorative; API values are never changed.
      if (translated === source && !catalog[source]) {
        const parts = source.match(/^([^A-Za-zÀ-ÿ0-9]*)(.*?)(\s*[:*]\s*)?$/);
        if (parts && catalog[parts[2].trim()]) translated = parts[1] + catalog[parts[2].trim()][language] + (parts[3] || '');
      }
    }
    return translated.replace(/\{(\w+)\}/g, (placeholder, key) => String(values?.[key] ?? placeholder));
  } };
}
